// MACHINERY V2 (Stroje) — readiness card for the profile left column (M2.9).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.9. Reference impl:
// design_handoff_machinery_v3/machinery-readiness.jsx (`ReadinessProfileCard`,
// `RdKvRow`, `RdProfileFragment`) + the shipped DR-3 card
// (components/readiness/MidasReadinessProfileCard.tsx).
//
// Sits between the identity card and the action buttons (M2.4 places it).
// Three rows — Midas docs / Live data / OEM package — plus the dot cluster.
//
// NO CTA (locked). Neither commercial CTA appears here; both live only in
// ReadinessPopupV2. The optional "View details" is a DISCLOSURE affordance
// that opens that popup, and it renders ONLY when the host wires
// `onOpenDetails` — this kit hard-wires no navigation.
//
// SCOPE OF "CONNECTED = STATIC TEXT, NOTHING CLICKABLE": that rule governs the
// COMPACT CARD surfaces — the capability-badge tooltip (CapTip gates its link
// on `presentation.interactive`) and the fleet card's Midas row. The PROFILE
// card keeps its "View details" in both the designer artboard
// (`ReadinessProfileCard` renders `ViewDetailsLink` unconditionally) and the
// shipped v1 card, because the profile is where a connected machine's document
// list is most wanted. Encoded here as: the affordance follows the host's
// `onOpenDetails`, not `interactive`. If the ruling changes, gate it on
// `presentation.interactive` — one line, right here.
//
// Nothing below re-derives readiness: every value comes off the shared
// presentation object the host also gave the badges, banner and popup.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import ReadinessDots from "./ReadinessDots";
import {
  MachineryV2ReadinessPresentation,
  RD,
  V2_DOC_TYPE_KEYS,
} from "./readinessPresentation";

interface KvRowProps {
  dot: string;
  hollow?: boolean;
  label: string;
  value: string;
  strong: boolean;
  valueColor?: string;
}

const KvRow: React.FC<KvRowProps> = ({ dot, hollow = false, label, value, strong, valueColor }) => (
  <div
    style={{
      display: "flex", alignItems: "baseline", gap: 10, padding: "5px 0", minWidth: 0,
    }}
  >
    <span
      style={{
        width: 7, height: 7, borderRadius: 999, flexShrink: 0,
        alignSelf: "center",
        background: hollow ? "transparent" : dot,
        border: hollow ? "1.5px solid " + RD.hollow : "none",
      }}
    />
    <span
      style={{
        width: 92, flexShrink: 0, fontFamily: "var(--font-mono)", fontSize: 10.5,
        letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--faint)",
      }}
    >
      {label}
    </span>
    <span
      style={{
        flex: 1, minWidth: 0, textAlign: "right",
        fontFamily: "var(--font-mono)", fontSize: 12.5,
        fontWeight: strong ? 600 : 400,
        color: valueColor || (strong ? "var(--ink)" : "var(--faint)"),
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}
    >
      {value}
    </span>
  </div>
);

export interface ReadinessProfileCardV2Props {
  presentation: MachineryV2ReadinessPresentation;
  oemPartnerName?: string | null;
  manufacturerName?: string | null;
  /** Opens ReadinessPopupV2. Omit and no affordance renders at all. */
  onOpenDetails?: (() => void) | null;
}

const ReadinessProfileCardV2: React.FC<ReadinessProfileCardV2Props> = ({
  presentation: p,
  oemPartnerName,
  manufacturerName,
  onOpenDetails = null,
}) => {
  const { translate } = useTranslation("myMachinery");

  // "Midas docs": lit TYPES when known, else the util's own doc phrase (the
  // total-only fallback), else "none yet". `docsSummary` resolves that mode —
  // this component does not decide it.
  const docsValue =
    p.docsSummary.kind === "types"
      ? p.docsSummary.types.map((t) => translate(V2_DOC_TYPE_KEYS[t])).join(" · ")
      : p.docsSummary.kind === "count"
      ? p.summary.docPart || ""
      : translate("myMachinery:noneYet");

  const liveValue =
    p.telemetry === "connected"
      ? translate("myMachinery:liveConnected")
      : p.telemetry === "possible"
      ? translate("myMachinery:liveAvailable")
      : translate("myMachinery:liveNotConnected");

  const oemValue = p.oemLit
    ? oemPartnerName || translate("myMachinery:oemAvailable")
    : translate("myMachinery:oemNotOfferedShort", {
        manufacturer: manufacturerName || translate("myMachinery:theManufacturer"),
      });

  return (
    <div
      style={{
        background: "var(--card)",
        border: "1px solid var(--border)",
        borderRadius: 10,
        boxShadow: "var(--shadow)",
        padding: "11px 15px",
        minWidth: 0,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <span className="eyebrow" style={{ color: "var(--muted)" }}>
          {translate("myMachinery:midasDataReadiness")}
        </span>
        <span style={{ flex: 1 }} />
        <ReadinessDots presentation={p} />
      </div>

      {/* Absence = an UNLIT row, never a hidden one: all three always render. */}
      <KvRow
        dot={p.midasLit ? RD.midas : RD.off}
        label={translate("myMachinery:midasDocs")}
        value={docsValue}
        strong={p.midasLit}
      />
      <KvRow
        dot={p.liveVisual === "lit" ? RD.live : RD.off}
        hollow={p.liveVisual === "hollow"}
        label={translate("myMachinery:liveData")}
        value={liveValue}
        strong={p.telemetry === "connected"}
        valueColor={
          p.telemetry === "connected"
            ? "color-mix(in oklab, var(--green) 78%, var(--ink))"
            : undefined
        }
      />
      <KvRow
        dot={p.oemLit ? RD.oem : RD.off}
        label={translate("myMachinery:oemPackage")}
        value={oemValue}
        strong={p.oemLit}
        valueColor={p.oemLit ? RD.goldText : undefined}
      />

      {onOpenDetails && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            borderTop: "1px solid var(--border-soft)",
            marginTop: 5,
            paddingTop: 7,
          }}
        >
          <button
            type="button"
            onClick={onOpenDetails}
            style={{
              padding: 0,
              border: 0,
              background: "transparent",
              cursor: "pointer",
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: "0.03em",
              color: RD.link,
              whiteSpace: "nowrap",
            }}
          >
            {translate("myMachinery:viewDetails")} ›
          </button>
        </div>
      )}
    </div>
  );
};

export default ReadinessProfileCardV2;
